import React, { useState, useRef, useEffect } from 'react';
import { useI18n } from '../../hooks/useI18n';
import { registerPhone, RegisterPhonePayload } from '../../api/authApi';
import { COUNTRIES, getCountryName } from './countries';

interface RegisterFormProps {
  onLoginClick: () => void;
  onSuccess: (phone: string) => void;
}



export const RegisterForm: React.FC<RegisterFormProps> = ({ onLoginClick, onSuccess }) => {
  const { t, lang } = useI18n();

  const [formData, setFormData] = useState<Omit<RegisterPhonePayload, 'phone'>>({
    email: '',
    firstName: '',
    lastName: '',
    consent1: false,
    consent2: false,
  });

  const [phone, setPhone] = useState('');
  const [selectedCountry, setSelectedCountry] = useState(COUNTRIES[0]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    if (fieldErrors[name]) {
      setFieldErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validate = (): boolean => {
    const errors: Record<string, string> = {};

    const phoneDigits = phone.replace(/\D/g, '');
    if (!phone) {
      errors.phone = t('err_phone_empty');
    } else if (phoneDigits.length < 8) {
      errors.phone = t('err_phone_format');
    }

    if (!formData.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      errors.email = t('err_email_invalid');
    }

    const nameRegex = /^[A-Za-zęĘóÓąĄśŚłŁżŻźŹćĆńŃ\s\-']+$/;

    if (!formData.firstName.trim()) {
      errors.firstName = t('err_firstname_empty');
    } else if (!nameRegex.test(formData.firstName.trim())) {
      errors.firstName = t('err_firstname_latin');
    }

    if (!formData.lastName.trim()) {
      errors.lastName = t('err_lastname_empty');
    } else if (!nameRegex.test(formData.lastName.trim())) {
      errors.lastName = t('err_lastname_latin');
    }

    if (!formData.consent1) {
      errors.consents = t('err_consent_required');
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const getFullPhone = () => {
    const phoneDigits = phone.replace(/\D/g, '');
    const dialDigits = selectedCountry.dialCode.replace(/\D/g, '');
    let finalPhone = phoneDigits;
    if (finalPhone.startsWith(dialDigits) && finalPhone.length > 8 + dialDigits.length) {
      finalPhone = finalPhone.substring(dialDigits.length);
    }
    return `${selectedCountry.dialCode}${finalPhone}`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isLoading) return;
    setError('');

    if (!validate()) return;

    const fullPhone = getFullPhone();

    setIsLoading(true);

    try {
      await registerPhone({
        phone: fullPhone,
        email: formData.email.trim(),
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        consent1: formData.consent1,
        consent2: formData.consent2,
      });

      onSuccess(fullPhone);
    } catch (err: any) {
      if (!err.response) {
        setError(t('err_network'));
      } else {
        const code = err.response?.data?.code;
        if (code === 'email_exists') {
          setFieldErrors(prev => ({ ...prev, email: t('err_email_exists') || 'Ten adres e-mail jest już zajęty.' }));
        } else if (code === 'rate_limit') {
          setError(err.response?.data?.message || t('err_rate_limit_10m') || 'Zbyt wiele prób. Odczekaj 10 minut.');
        } else {
          setError(err.response?.data?.message || t('err_register_failed') || 'Rejestracja nie powiodła się. Spróbuj ponownie.');
        }
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="cab-auth-form cab-reg-phone-form">
      <div className="cab-auth-header-wrapper">
        <h2>{t('reg_phone_title')}</h2>
        <p className="cab-sub">{t('reg_phone_sub')}</p>
      </div>

      <form onSubmit={handleSubmit} noValidate>
        {/* Telefon */}
        <div className="cab-field" ref={containerRef} style={{ zIndex: isDropdownOpen ? 100 : 1 }}>
          <div className={`cab-phone-input-wrapper ${fieldErrors.phone ? 'has-error' : ''} ${isDropdownOpen ? 'dropdown-open' : ''}`}>
            <div
              className="cab-phone-prefix"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setIsDropdownOpen(!isDropdownOpen);
              }}
            >
              {selectedCountry.flagNode}
              <span>{selectedCountry.dialCode}</span>
              <svg
                width="12" height="8" viewBox="0 0 12 8" fill="none"
                style={{ transform: isDropdownOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s', pointerEvents: 'none', marginLeft: '2px' }}
              >
                <path d="M1 1.5L6 6.5L11 1.5" stroke="#361777" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <input
              type="tel"
              className="cab-phone-input"
              placeholder="XXX XXX XXX"
              value={phone}
              onChange={(e) => {
                setPhone(e.target.value);
                if (fieldErrors.phone) setFieldErrors(prev => ({ ...prev, phone: '' }));
              }}
              disabled={isLoading}
            />
          </div>
          {fieldErrors.phone && <div className="cab-field-error">{fieldErrors.phone}</div>}

          {isDropdownOpen && (
            <div className="cab-country-dropdown">
              {COUNTRIES.map((c) => (
                <div
                  key={c.code}
                  className={`cab-country-option ${c.code === selectedCountry.code ? 'selected' : ''}`}
                  onClick={() => {
                    setSelectedCountry(c);
                    setIsDropdownOpen(false);
                  }}
                >
                  <div className="cab-country-flag-wrap">{c.flagNode}</div>
                  <span className="cab-country-name">{getCountryName(c.code, lang, c.name)}</span>
                  <span className="cab-country-dial">{c.dialCode}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* E-mail */}
        <div className="cab-field">
          <input
            type="email"
            name="email"
            className={`cab-input ${fieldErrors.email ? 'has-error' : ''}`}
            placeholder="E-mail*"
            value={formData.email}
            onChange={handleChange}
            disabled={isLoading}
          />
          {fieldErrors.email && <div className="cab-field-error">{fieldErrors.email}</div>}
        </div>

        {/* Imię */}
        <div className="cab-field">
          <input
            type="text"
            name="firstName"
            className={`cab-input ${fieldErrors.firstName ? 'has-error' : ''}`}
            placeholder={`${t('reg_firstname')}*`}
            value={formData.firstName}
            onChange={handleChange}
            disabled={isLoading}
          />
          {fieldErrors.firstName && <div className="cab-field-error">{fieldErrors.firstName}</div>}
        </div>

        {/* Nazwisko */}
        <div className="cab-field">
          <input
            type="text"
            name="lastName"
            className={`cab-input ${fieldErrors.lastName ? 'has-error' : ''}`}
            placeholder={`${t('reg_lastname')}*`}
            value={formData.lastName}
            onChange={handleChange}
            disabled={isLoading}
          />
          {fieldErrors.lastName && <div className="cab-field-error">{fieldErrors.lastName}</div>}
        </div>

        {/* Zgody */}
        <div className="cab-consents">
          <label className={`cab-consent ${fieldErrors.consents && !formData.consent1 ? 'has-error' : ''}`}>
            <input
              type="checkbox"
              name="consent1"
              checked={formData.consent1}
              onChange={handleChange}
              disabled={isLoading}
            />
            <span className="cab-consent-text" dangerouslySetInnerHTML={{ __html: t('consent1_text') }} />
          </label>

          <label className={`cab-consent`}>
            <input
              type="checkbox"
              name="consent2"
              checked={formData.consent2}
              onChange={handleChange}
              disabled={isLoading}
            />
            <span className="cab-consent-text" dangerouslySetInnerHTML={{ __html: t('consent2_text') }} />
          </label>

          {fieldErrors.consents && (
            <div className="cab-field-error" style={{ marginTop: '4px' }}>{fieldErrors.consents}</div>
          )}
        </div>

        {error && (
          <div className="cab-error-msg" style={{ marginTop: '12px' }}>
            {error}
            <div style={{ marginTop: '8px' }}>
              <span 
                onClick={() => {
                  if (validate()) {
                    setError('');
                    onSuccess(getFullPhone());
                  } else {
                    setError(t('err_fill_all_to_enter_code'));
                  }
                }} 
                style={{ textDecoration: 'underline', cursor: 'pointer', color: '#361777', fontWeight: 600 }}
              >
                {t('btn_have_code')}
              </span>
            </div>
          </div>
        )}

        <button
          type="submit"
          className="cab-btn-primary"
          disabled={isLoading}
          style={{ marginTop: '24px' }}
        >
          {isLoading ? '...' : t('send_sms_btn')}
        </button>
        
        <button
          className="cab-btn-outline"
          onClick={onLoginClick}
          type="button"
          style={{ marginTop: '16px' }}
        >
          {t('reg_login_link')}
        </button>
      </form>

      <style>{`
        .cab-reg-phone-form * {
          box-sizing: border-box;
        }
        .cab-reg-phone-form.cab-auth-form {
          background: #F8F6FC;
          border-radius: 24px;
          padding: 48px 40px;
          width: 100%;
          max-width: 644px;
          margin: 0 auto;
          font-family: inherit;
          margin-top: 50px;
        }
        .cab-reg-phone-form h2 {
          font-size: 28px;
          font-weight: 800;
          color: #361777;
          margin-bottom: 12px;
          text-align: center;
        }
        .cab-reg-phone-form .cab-sub {
          font-size: 16px;
          color: #5A4E7A;
          text-align: center;
          line-height: 1.4;
          margin: 0 auto 32px;
          max-width: 380px;
          font-weight: 500;
        }
        .cab-field {
          text-align: left;
          margin-bottom: 16px;
          position: relative;
        }
        .cab-input {
          width: 100%;
          height: 56px;
          border-radius: 100px;
          background: #FFFFFF;
          border: 1px solid transparent;
          padding: 0 24px;
          font-size: 16px;
          color: #361777;
          font-family: inherit;
          font-weight: 500;
          transition: border-color 0.2s;
        }
        .cab-input.has-error {
          border-color: #FF4D4F;
        }
        .cab-input:focus {
          outline: none;
          border-color: #361777;
        }
        .cab-input::placeholder {
          color: #A49BB6;
          font-weight: 500;
        }
        .cab-field-error {
          color: #FF4D4F;
          font-size: 13px;
          margin-top: 6px;
          font-weight: 500;
          padding-left: 20px;
        }
        /* Phone input */
        .cab-phone-input-wrapper {
          display: flex;
          align-items: center;
          background: #FFFFFF;
          border-radius: 100px;
          padding: 4px;
          height: 56px;
          border: 1px solid transparent;
          transition: border-color 0.2s;
        }
        .cab-phone-input-wrapper:focus-within,
        .cab-phone-input-wrapper.dropdown-open {
          border-color: #361777;
        }
        .cab-phone-input-wrapper.has-error {
          border-color: #FF4D4F;
        }
        .cab-phone-prefix {
          display: flex;
          align-items: center;
          background: #EBE4F7;
          color: #361777;
          font-weight: 600;
          font-size: 15px;
          padding: 0 16px;
          border-radius: 100px;
          height: 44px;
          gap: 8px;
          cursor: pointer;
          user-select: none;
        }
        .cab-phone-prefix:hover {
          background: #E0D6F2;
        }
        .cab-phone-input {
          flex: 1;
          border: none;
          background: transparent;
          padding: 0 16px;
          font-size: 16px;
          color: #361777;
          height: 100%;
          font-weight: 500;
          font-family: inherit;
        }
        .cab-phone-input:focus { outline: none; }
        .cab-phone-input::placeholder { color: #A49BB6; }
        .cab-flag-icon {
          border-radius: 50%;
          display: block;
          pointer-events: none;
        }
        .cab-flag-emoji {
          font-size: 18px;
          pointer-events: none;
        }
        .cab-country-dropdown {
          position: absolute;
          top: calc(100% + 8px);
          left: 0;
          width: 100%;
          background: #FFFFFF;
          border-radius: 20px;
          box-shadow: 0 10px 40px rgba(45, 21, 97, 0.08);
          border: 1px solid #EBE4F7;
          z-index: 1000;
          max-height: 240px;
          overflow-y: auto;
          padding: 12px 0;
        }
        .cab-country-option {
          display: flex;
          align-items: center;
          padding: 12px 20px;
          cursor: pointer;
          transition: background 0.2s;
          gap: 12px;
        }
        .cab-country-option:hover { background: #F8F6FC; }
        .cab-country-option.selected { background: #EBE4F7; }
        .cab-country-flag-wrap {
          display: flex;
          align-items: center;
          width: 24px;
        }
        .cab-country-name { flex: 1; font-size: 15px; color: #361777; font-weight: 600; }
        .cab-country-dial { color: #A49BB6; font-size: 15px; font-weight: 600; }
        /* Consents */
        .cab-consents {
          margin-top: 20px;
          margin-bottom: 8px;
          display: flex;
          flex-direction: column;
          gap: 16px;
        }
        .cab-consent {
          display: flex;
          gap: 12px;
          align-items: flex-start;
          cursor: pointer;
          text-align: left;
        }
        .cab-consent-text {
          font-size: 13px;
          color: #5A4E7A;
          line-height: 1.5;
          font-weight: 500;
        }
        .cab-consent-text a {
          color: #5A4E7A;
          text-decoration: underline;
          font-weight: 700;
        }
        .cab-consent-text a:hover {
          color: #361777;
        }
        .cab-consent.has-error .cab-consent-text {
          color: #FF4D4F;
        }
        .cab-consent input[type="checkbox"] {
          appearance: none;
          -webkit-appearance: none;
          width: 20px;
          height: 20px;
          border: 1.5px solid #D1C9E3;
          border-radius: 6px;
          background-color: #fff;
          cursor: pointer;
          position: relative;
          flex-shrink: 0;
          margin-top: 0;
          transition: all 0.2s;
        }
        .cab-consent input[type="checkbox"]:checked {
          background-color: #361777;
          border-color: #361777;
        }
        .cab-consent input[type="checkbox"]:checked::after {
          content: "";
          position: absolute;
          left: 6px;
          top: 2px;
          width: 5px;
          height: 10px;
          border: solid white;
          border-width: 0 2px 2px 0;
          transform: rotate(45deg);
        }
        .cab-consent input[type="checkbox"]:hover {
          border-color: #361777;
        }
        .cab-consent.has-error input[type="checkbox"] {
          border-color: #FF4D4F;
        }
        /* CTA */
        .cab-btn-primary {
          width: 100%;
          height: 56px;
          border-radius: 100px;
          background: #361777;
          color: #fff;
          font-size: 16px;
          font-weight: 700;
          border: none;
          cursor: pointer;
          transition: opacity 0.2s;
          display: flex;
          align-items: center;
          justify-content: center;
          font-family: inherit;
        }
        .cab-btn-primary:hover { opacity: 0.9; }
        .cab-btn-primary:disabled { opacity: 0.6; cursor: not-allowed; }
        
        .cab-btn-outline {
          width: 100%;
          height: 56px;
          border-radius: 100px;
          background: transparent;
          color: #361777;
          font-size: 16px;
          font-weight: 700;
          border: 1px solid #361777;
          cursor: pointer;
          transition: all 0.2s;
          display: flex;
          align-items: center;
          justify-content: center;
          font-family: inherit;
        }
        .cab-btn-outline:hover {
          background: rgba(54, 23, 119, 0.05);
        }

        .cab-error-msg {
          color: #FF4D4F;
          font-size: 14px;
          font-weight: 500;
          text-align: center;
        }
        @media (max-width: 480px) {
          .cab-reg-phone-form.cab-auth-form {
            padding: 32px 20px;
            border-radius: 20px;
          }
        }
      `}</style>
    </div>
  );
};
